import {
  CTS_WORKFLOW_STATES, CTS_OPEN_STATES, CTS_IMPLEMENTATION_STATUS_LABEL, CTS_NOT_YET_ASSESSED_LABEL,
  type CtsWorkflowState,
} from '@grc/shared';
import type { Task, TasksWorkspace } from '@/api/types/cts-tasks.types';

// ── TC4 — the "what needs attention" insights, derived ───────────────────────────────────────
//
// ⚠ EVERY FIGURE HERE IS A COUNT OF ROWS THE TENANT CAN GO AND LOOK AT. Nothing is scored,
// weighted or trended: there is no stored historical series anywhere in the model — derived
// progress is computed live and never written down — so a sparkline or a week-over-week delta
// would be inventing movement out of a single snapshot. Point-in-time only, and the tiles say so
// by carrying a scope word ("team", "mine") rather than a direction.

export interface InsightTile {
  key: string;
  value: number;
  label: string;
  /** The scope of the count — "mine" versus "team" is the difference between a queue and a report. */
  caption: string;
  tone: 'info' | 'review' | 'overdue' | 'due' | 'returned' | 'unowned';
}

const isOpen = (t: Task) => (CTS_OPEN_STATES as readonly string[]).includes(t.workflow_status);
const overdue = (t: Task, now: Date) => t.due_at !== null && new Date(t.due_at) < now && isOpen(t);
const dueWithin = (t: Task, now: Date, days: number) => {
  if (t.due_at === null || !isOpen(t)) return false;
  const d = new Date(t.due_at);
  return d >= now && d.getTime() - now.getTime() <= days * 86_400_000;
};

/**
 * ⚠ "Awaiting my review" is only ever non-zero for a REVIEWER. For anyone else the work sitting at
 * Submitted for Review is not theirs to decide, and counting it under "my" would tell them to act
 * on something the machine will refuse them.
 */
export function buildInsights(ws: TasksWorkspace, now = new Date()): InsightTile[] {
  const mineOpen = ws.mine.filter(isOpen);
  const awaitingReview = ws.viewer_is_reviewer
    ? ws.all.filter((t) => t.workflow_status === 'submitted_for_review')
    : [];
  return [
    { key: 'mine', value: mineOpen.length, label: 'Assigned to me', caption: 'open items', tone: 'info' },
    { key: 'review', value: awaitingReview.length, label: 'Awaiting my review', caption: 'need my decision', tone: 'review' },
    { key: 'overdue', value: ws.all.filter((t) => overdue(t, now)).length, label: 'Overdue', caption: 'team · past due', tone: 'overdue' },
    { key: 'due7', value: ws.all.filter((t) => dueWithin(t, now, 7)).length, label: 'Due within 7 days', caption: 'team · this week', tone: 'due' },
    { key: 'returned', value: ws.all.filter((t) => t.workflow_status === 'returned').length, label: 'Returned', caption: 'team · needs rework', tone: 'returned' },
    { key: 'unassigned', value: ws.all.filter((t) => t.assignee_user_id === null && isOpen(t)).length, label: 'Unassigned', caption: 'team · no owner', tone: 'unowned' },
  ];
}

export interface DistributionSegment { key: string; label: string; n: number; cls: string }

const WORKFLOW_CLS: Record<CtsWorkflowState, string> = {
  unassigned: 'bg-slate-500', assigned: 'bg-sky-400', in_progress: 'bg-blue-400',
  submitted_for_review: 'bg-violet-400', returned: 'bg-amber-400', approved: 'bg-teal-400',
  completed: 'bg-emerald-400', reopened: 'bg-orange-400',
};

/**
 * ⚠ THE WORKFLOW DISTRIBUTION IS OVER TASKS, AND `unassigned` IS THE ONE STATE IT CANNOT CONTAIN.
 *
 * `unassigned` is the ABSENCE of an assignment row — there is nothing to count. A distribution
 * showing it would either be counting rows that should not exist, or silently switching its unit
 * from tasks to controls halfway through the legend. The Controls workspace is where a
 * no-assignment control is visible, because there the unit IS the control.
 *
 * ⓘ What the surface CAN honestly show under a task distribution is work with no OWNER — an
 * assigned row whose assignee is null — and that is a different fact with a different name.
 */
export function workflowDistribution(tasks: Task[]): DistributionSegment[] {
  return CTS_WORKFLOW_STATES
    .filter((s) => s !== 'unassigned')
    .map((s) => ({
      key: s,
      label: s.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
      n: tasks.filter((t) => t.workflow_status === s).length,
      cls: WORKFLOW_CLS[s],
    }))
    .filter((seg) => seg.n > 0);
}

const POSTURE_ORDER = [
  ['implemented', CTS_IMPLEMENTATION_STATUS_LABEL.implemented, 'bg-cert-valid-fg'],
  ['partially_implemented', CTS_IMPLEMENTATION_STATUS_LABEL.partially_implemented, 'bg-cert-warn-fg'],
  ['not_implemented', CTS_IMPLEMENTATION_STATUS_LABEL.not_implemented, 'bg-cert-expired-fg'],
  ['unknown', CTS_IMPLEMENTATION_STATUS_LABEL.unknown, 'bg-slate-500'],
  ['not_applicable', CTS_IMPLEMENTATION_STATUS_LABEL.not_applicable, 'bg-text-faint/40'],
] as const;

/**
 * ⚠ POSTURE IS A SEPARATE AXIS AND THE PANEL SAYS SO. It sits beside the workflow distribution
 * rather than inside it: "In Progress" and "Not Implemented" describe different things about the
 * same row, and one stacked bar carrying both would invite the reader to add them up.
 *
 * ⚠ A task whose control has NO assessment is `not_started` — never counted as a zero and never
 * folded into "Not Implemented". Nobody has measured it.
 */
export function postureDistribution(tasks: Task[]): DistributionSegment[] {
  // ⚠ CAPR S2 — the DERIVED implementation status (the latest approved decision), never the deprecated
  //   `posture`. No approved decision is "Not Yet Assessed" (D-CAPR-STATUS-SET retired "Not Started").
  const segs: DistributionSegment[] = POSTURE_ORDER.map(([value, label, cls]) => ({
    key: value, label, n: tasks.filter((t) => t.implementation_status === value).length, cls,
  }));
  segs.splice(3, 0, {
    key: 'not_yet_assessed', label: CTS_NOT_YET_ASSESSED_LABEL,
    n: tasks.filter((t) => t.implementation_status === null).length, cls: 'bg-text-faint/25',
  });
  return segs.filter((s) => s.n > 0);
}
